import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Paperclip } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, todayISO } from "@/lib/api";
import { Field, EmptyState } from "@/components/Field";
import { AttachmentList, CATEGORIES } from "@/components/AttachmentList";

const blank = () => ({ title: "", category: "esami", date: todayISO(), visible: false });

export function AttachmentsTab({ rec, reload }) {
  const [f, setF] = useState(blank());
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Seleziona un file");
    if (file.size > 15 * 1024 * 1024) return toast.error("File troppo grande (max 15 MB)");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => fd.append(k, String(v)));
    setUploading(true);
    try {
      await api.post(`/patients/${rec.patient.id}/attachments`, fd);
      toast.success("Documento caricato", { description: f.visible ? "Il paziente è stato avvisato" : "Nascosto al paziente" });
      setF(blank());
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (a) => {
    try {
      await api.put(`/attachments/${a.id}/visibility`, { visible: !a.visible });
      toast.success(a.visible ? "Documento nascosto al paziente" : "Documento visibile al paziente");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.title}"?`)) return;
    await api.delete(`/attachments/${a.id}`);
    reload();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <form onSubmit={upload} className="card h-fit space-y-4" data-testid="attachment-form">
        <h3 className="text-lg font-bold">Carica documento</h3>
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[#b8956f] bg-[#edf2ef]/40 p-6 text-center hover:bg-[#edf2ef]">
          <Upload className="h-6 w-6 text-[#3d655b]" />
          <span className="text-sm font-semibold">{file ? file.name : "Scegli PDF o immagine"}</span>
          <span className="text-xs text-slate-500">PDF, JPG, PNG, WEBP, HEIC · max 15 MB</span>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden"
            onChange={(e) => { const x = e.target.files?.[0] || null; setFile(x); if (x && !f.title) setF({ ...f, title: x.name.replace(/\.[^.]+$/, "") }); }}
            data-testid="attachment-file-input" />
        </label>
        <Field label="Titolo"><input className="field" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Es. Emocromo completo" data-testid="attachment-title-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo">
            <select className="field" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="attachment-category-select">
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Data"><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="attachment-date-input" /></Field>
        </div>
        <div className="flex items-center justify-between rounded-xl bg-[#edf2ef] px-4 py-3">
          <span className="text-sm font-medium text-[#2f4f47]">Visibile al paziente</span>
          <Switch checked={f.visible} onCheckedChange={(v) => setF({ ...f, visible: v })} data-testid="attachment-visible-switch" />
        </div>
        <button className="btn-primary w-full" disabled={uploading} data-testid="attachment-upload-button">
          <Upload className="h-4 w-4" /> {uploading ? "Caricamento…" : "Carica"}
        </button>
      </form>
      <div className="lg:col-span-2">
        {rec.attachments.length ? (
          <AttachmentList items={rec.attachments} onToggle={toggle} onDelete={remove} prefix="attachment" />
        ) : <EmptyState icon={Paperclip} title="Nessun documento" text="Carica esami del sangue, referti o altri PDF." testid="attachments-empty" />}
      </div>
    </div>
  );
}
